import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    Chip,
    IconButton,
    Paper,
    Grid,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// project imports
import { updateTestResults, addExecution, updateFunctionMetrics } from 'store/slices/lambdaFunctions';

// icons
import {
    IconX,
    IconLoader2,
    IconCode,
    IconBug,
    IconClipboard,
    IconHistory,
    IconFile,
    IconAlertCircle,
    IconCircleCheck
} from '@tabler/icons-react';

// Sample test templates
const testTemplates = {
    calculate_discount: {
        price: 100,
        quantity: 5,
        customer_tier: 'gold',
        promo_code: 'SUMMER20'
    },
    process_payment: {
        amount: 99.99,
        currency: 'USD',
        payment_method: {
            type: 'credit_card',
            card_number_last4: '4242',
            expiry_month: 12,
            expiry_year: 2025
        },
        customer_id: 'cust_123456'
    },
    analyze_sentiment: {
        text: 'I absolutely love this product! It exceeded all my expectations.',
        language: 'en',
        detailed: true
    },
    generate_report: {
        report_type: 'sales',
        data: {
            start_date: '2024-07-01',
            end_date: '2024-07-17',
            metrics: ['revenue', 'units_sold', 'conversion_rate']
        },
        format: 'pdf'
    },
    validate_data: {
        data: {
            name: 'John Doe',
            email: 'john.doe@example.com',
            age: 30
        },
        schema: {
            name: { type: 'string', required: true },
            email: { type: 'string', format: 'email', required: true },
            age: { type: 'number', min: 0, max: 120 }
        }
    }
};

// ==============================|| FUNCTION TEST MODAL ||============================== //

const FunctionTestModal = ({ open, onClose, function: func }) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [tabValue, setTabValue] = useState(0);
    const [inputData, setInputData] = useState(JSON.stringify(testTemplates[func?.name] || {}, null, 2));
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('custom');
    const [executionHistory, setExecutionHistory] = useState([]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleTemplateChange = (event) => {
        const template = event.target.value;
        setSelectedTemplate(template);
        if (template !== 'custom' && testTemplates[func?.name]) {
            setInputData(JSON.stringify(testTemplates[func.name], null, 2));
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);

        // Validate JSON
        try {
            const parsedInput = JSON.parse(inputData);

            // Simulate API call
            setTimeout(() => {
                const success = Math.random() > 0.1; // 90% success rate
                const executionTime = Math.floor(Math.random() * 100) + 10;

                const result = {
                    success,
                    request_id: `test_${Date.now()}`,
                    execution_time_ms: executionTime,
                    timestamp: new Date().toISOString()
                };

                if (success) {
                    // Simulate successful response
                    result.result = generateMockResponse(func.name, parsedInput);
                    result.metadata = {
                        billed_duration_ms: Math.ceil(executionTime / 100) * 100,
                        memory_used_mb: Math.floor(Math.random() * 128) + 32,
                        cold_start: Math.random() > 0.8
                    };
                } else {
                    // Simulate error
                    result.error = {
                        type: 'ValidationError',
                        message: 'Invalid input parameter: customer_tier must be one of [bronze, silver, gold, platinum]',
                        stack_trace: generateMockStackTrace()
                    };
                }

                setTestResult(result);
                dispatch(updateTestResults({ functionId: func.id, result }));

                // Add to execution history
                const execution = {
                    id: `exec_${Date.now()}`,
                    function_id: func.id,
                    request_id: result.request_id,
                    status: success ? 'success' : 'failed',
                    execution_time_ms: executionTime,
                    input_data: parsedInput,
                    output_data: success ? result.result : undefined,
                    error_message: !success ? result.error.message : undefined,
                    created_at: result.timestamp
                };

                dispatch(addExecution(execution));
                setExecutionHistory([execution, ...executionHistory].slice(0, 10));

                // Update function metrics
                if (success) {
                    dispatch(
                        updateFunctionMetrics({
                            functionId: func.id,
                            metrics: {
                                total_executions_24h: func.metrics.total_executions_24h + 1,
                                last_execution: result.timestamp
                            }
                        })
                    );
                }

                setTesting(false);
            }, 1500);
        } catch (error) {
            setTestResult({
                success: false,
                error: {
                    type: 'JSONParseError',
                    message: 'Invalid JSON input: ' + error.message
                }
            });
            setTesting(false);
        }
    };

    const generateMockResponse = (functionName, input) => {
        switch (functionName) {
            case 'calculate_discount':
                const discount = input.customer_tier === 'gold' ? 15 : 10;
                return {
                    original_price: input.price,
                    quantity: input.quantity,
                    discount_percentage: discount,
                    discount_amount: (input.price * input.quantity * discount) / 100,
                    final_price: input.price * input.quantity * (1 - discount / 100),
                    applied_promo: input.promo_code ? true : false
                };
            case 'process_payment':
                return {
                    transaction_id: `txn_${Date.now()}`,
                    status: 'completed',
                    amount: input.amount,
                    currency: input.currency,
                    processing_fee: input.amount * 0.029 + 0.3,
                    net_amount: input.amount - (input.amount * 0.029 + 0.3),
                    timestamp: new Date().toISOString()
                };
            case 'analyze_sentiment':
                return {
                    sentiment: 'positive',
                    confidence: 0.92,
                    scores: {
                        positive: 0.92,
                        neutral: 0.06,
                        negative: 0.02
                    },
                    entities: ['product'],
                    keywords: ['love', 'exceeded', 'expectations']
                };
            default:
                return { status: 'success', data: input };
        }
    };

    const generateMockStackTrace = () => {
        return `Traceback (most recent call last):
  File "/var/task/handler.py", line 15, in lambda_handler
    result = process_request(event)
  File "/var/task/handler.py", line 32, in process_request
    validate_input(data)
  File "/var/task/validators.py", line 12, in validate_input
    raise ValidationError(f"Invalid input parameter: {error}")
ValidationError: Invalid input parameter: customer_tier must be one of [bronze, silver, gold, platinum]`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    if (!func) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '80vh'
                }
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconBug size={24} />
                        <Typography variant="h4">Test Function: {func.name}</Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <IconX size={20} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Alert severity="info">
                        <Typography variant="body2">
                            Endpoint: <code>{func.endpoint}</code>
                        </Typography>
                    </Alert>
                </Box>

                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label="Test Input" icon={<IconCode size={18} />} iconPosition="start" />
                    <Tab label="Execution History" icon={<IconHistory size={18} />} iconPosition="start" />
                </Tabs>

                {tabValue === 0 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Input Parameters
                                    </Typography>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Template</InputLabel>
                                        <Select value={selectedTemplate} label="Template" onChange={handleTemplateChange}>
                                            <MenuItem value="custom">Custom</MenuItem>
                                            {testTemplates[func.name] && <MenuItem value="default">Default Template</MenuItem>}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    {func.parameters.map((param, index) => (
                                        <Box key={index} sx={{ mb: 1 }}>
                                            <Typography variant="caption" color="textSecondary">
                                                {param.name} ({param.type}) {param.required && '*'}
                                            </Typography>
                                            {param.description && (
                                                <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                                                    {param.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={12}
                                    value={inputData}
                                    onChange={(e) => setInputData(e.target.value)}
                                    placeholder="Enter JSON input..."
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem'
                                        }
                                    }}
                                />

                                <Box display="flex" gap={1} mt={2}>
                                    <Button
                                        variant="contained"
                                        startIcon={testing ? <IconLoader2 className="animate-spin" /> : <IconBug />}
                                        onClick={handleTest}
                                        disabled={testing || !inputData}
                                        fullWidth
                                    >
                                        {testing ? 'Testing...' : 'Run Test'}
                                    </Button>
                                    <IconButton onClick={() => copyToClipboard(inputData)}>
                                        <IconClipboard size={20} />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                                    Test Result
                                </Typography>

                                {testing && (
                                    <Box textAlign="center" py={8}>
                                        <IconLoader2 size={48} className="animate-spin" style={{ opacity: 0.5 }} />
                                        <Typography variant="body2" sx={{ mt: 2, opacity: 0.5 }}>
                                            Executing function...
                                        </Typography>
                                    </Box>
                                )}

                                {!testing && testResult && (
                                    <>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            {testResult.success ? (
                                                <>
                                                    <IconCircleCheck size={20} color={theme.palette.success.main} />
                                                    <Typography variant="body2" color="success.main">
                                                        Success
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <IconAlertCircle size={20} color={theme.palette.error.main} />
                                                    <Typography variant="body2" color="error.main">
                                                        Failed
                                                    </Typography>
                                                </>
                                            )}
                                            <Chip label={`${testResult.execution_time_ms}ms`} size="small" variant="outlined" />
                                            {testResult.request_id && (
                                                <Chip
                                                    label={testResult.request_id}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                                />
                                            )}
                                        </Box>

                                        {testResult.success ? (
                                            <>
                                                <SyntaxHighlighter
                                                    language="json"
                                                    style={vscDarkPlus}
                                                    customStyle={{
                                                        margin: 0,
                                                        borderRadius: 4,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {JSON.stringify(testResult.result, null, 2)}
                                                </SyntaxHighlighter>

                                                {testResult.metadata && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Metadata:
                                                        </Typography>
                                                        <Box display="flex" gap={2} mt={1}>
                                                            <Chip
                                                                label={`Billed: ${testResult.metadata.billed_duration_ms}ms`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`Memory: ${testResult.metadata.memory_used_mb}MB`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            {testResult.metadata.cold_start && (
                                                                <Chip label="Cold Start" size="small" variant="outlined" color="warning" />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Alert severity="error" sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        {testResult.error.type}
                                                    </Typography>
                                                    <Typography variant="body2">{testResult.error.message}</Typography>
                                                </Alert>

                                                {testResult.error.stack_trace && (
                                                    <SyntaxHighlighter
                                                        language="python"
                                                        style={vscDarkPlus}
                                                        customStyle={{
                                                            margin: 0,
                                                            borderRadius: 4,
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        {testResult.error.stack_trace}
                                                    </SyntaxHighlighter>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}

                                {!testing && !testResult && (
                                    <Box textAlign="center" py={8}>
                                        <IconFile size={48} style={{ opacity: 0.3 }} />
                                        <Typography variant="body2" sx={{ mt: 2, opacity: 0.5 }}>
                                            Run a test to see results
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {tabValue === 1 && (
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>
                            Recent Executions
                        </Typography>

                        {executionHistory.length > 0 ? (
                            <Box>
                                {executionHistory.map((execution, index) => (
                                    <Box
                                        key={execution.id}
                                        sx={{
                                            p: 2,
                                            mb: 1,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {execution.status === 'success' ? (
                                                    <IconCircleCheck size={16} color={theme.palette.success.main} />
                                                ) : (
                                                    <IconAlertCircle size={16} color={theme.palette.error.main} />
                                                )}
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                    {execution.request_id}
                                                </Typography>
                                                <Chip label={`${execution.execution_time_ms}ms`} size="small" variant="outlined" />
                                            </Box>
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(execution.created_at).toLocaleString()}
                                            </Typography>
                                        </Box>

                                        {execution.error_message && (
                                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                                {execution.error_message}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="textSecondary">
                                    No execution history for this session
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FunctionTestModal;
